import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { FetchOracleOrderStatusesInput } from '@erp/rpc';

/**
 * Mutation hook for fetching Oracle order statuses from the ERP.
 *
 * Calls the backend which in turn queries Oracle for the order numbers
 * of sales orders that have been synced but don't yet have an Oracle
 * order number stored. The backend persists the results so subsequent
 * page loads return the cached values.
 *
 * On success the caller should invalidate the drafts query so the table
 * re-renders with the newly populated Oracle order numbers.
 */
export function useFetchOracleOrderStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: FetchOracleOrderStatusesInput) => {
      return await rpcClient.accountsReceivable.salesOrders.fetchOracleOrderStatuses(
        input,
      );
    },
    onSuccess: () => {
      // Invalidate the drafts list so the table picks up the newly
      // persisted Oracle order numbers without a manual refresh.
      void queryClient.invalidateQueries({
        queryKey: ['accounts-receivable/staged-purchase-orders-v2'],
      });
    },
  });
}

/**
 * OMS v3 variant of {@link useFetchOracleOrderStatuses}.
 *
 * Same backend call, but invalidates the v3 review-page queries instead of the
 * v2 drafts list: the draft detail (`salesOrdersV3.get`, so the lifecycle stage
 * refreshes) and each polled `salesOrders.get` (so the newly persisted
 * `originId` / `originSyncStatus` show up). Pass the draft id whose review page
 * triggered the poll.
 */
export function useFetchOracleOrderStatusesV3(draftId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: FetchOracleOrderStatusesInput) => {
      return await rpcClient.accountsReceivable.salesOrders.fetchOracleOrderStatuses(
        input,
      );
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: draftId },
        }),
      });
      for (const salesOrderId of variables.salesOrderIds) {
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.salesOrders.get.key({
            input: { id: salesOrderId },
          }),
        });
      }
    },
  });
}
